import { MEAL_TYPES, MEAL_TYPE_LABELS, mealTypeOf, type MealType } from "./mealTypes";
import { NON_COOK_LABEL, type PlanMeal } from "./mealPlanShape";

/**
 * "Send to my people" — the week's plan as a text she sends as herself. #1093.
 *
 * ⚠️ NO EM-DASHES ANYWHERE IN THE OUTPUT. Standing rule 8, and it bites harder
 * here than anywhere else in the app: this is not Kin's copy appearing in Kin's
 * UI, it is a message that will land in her partner's Messages thread under HER
 * name. An em-dash is a tell that a robot wrote it. `stripEmDashes` is applied
 * to the whole assembled body as a last pass, so it cannot be missed by a new
 * branch someone adds later, and a test pins the assembled output.
 *
 * Reuses the meal shape and the non-cook labels from FridgeCard rather than
 * redefining them — the two surfaces must not drift on what "Thursday is
 * takeout" looks like.
 */

/**
 * Replace every dash that reads as prose punctuation with a comma or a plain
 * hyphen. En-dashes are included: they are just as much of a tell and nothing
 * in Kin's voice needs one.
 */
export function stripEmDashes(text: string): string {
  return (
    text
      // " — " as a clause break becomes a comma, which is what she would type.
      .replace(/\s*[—–]\s*/g, ", ")
      // The rule above appends a space unconditionally, which leaves a trailing
      // ", " when the dash ended a line. Harmless-looking, but it ships as
      // visible trailing whitespace in a message she sends, so clean it per
      // line rather than relying on a single trim of the whole body.
      .replace(/[ \t]+$/gm, "")
  );
}

function mealText(m: PlanMeal): string {
  if (m.night_type && NON_COOK_LABEL[m.night_type]) {
    const base = NON_COOK_LABEL[m.night_type];
    return m.night_reason ? `${base}, ${m.night_reason}` : base;
  }
  const name = m.meal_name || m.meal || "";
  // School lunches summarise to the main rather than listing all four slots;
  // the full breakdown belongs on the fridge card, not in a text.
  if (!name && m.main) return m.main;
  return name;
}

/**
 * School lunches are summarised, NOT expanded per kid — unless the kids are
 * actually getting different food, in which case the difference is the whole
 * point of mentioning it.
 */
function schoolLunchText(m: PlanMeal): string {
  const base = mealText(m);
  const kids = m.per_kid ?? [];
  if (kids.length < 2) return base;

  const mains = kids.map((k) => k.main).filter(Boolean);
  const allSame = mains.length > 0 && new Set(mains).size === 1;
  if (allSame) return base;

  return kids.map((k) => `${k.kid}: ${k.main || base}`).join(", ");
}

export interface PlanTextInput {
  meals: PlanMeal[];
  weekSummary?: string | null;
}

/**
 * Build the message body.
 *
 * Compact on purpose: a week header, then one line per day per active meal
 * type. Long enough to be useful, short enough that nobody scrolls a text.
 */
export function buildMealPlanText(input: PlanTextInput): string {
  const lines: string[] = ["This week's meals 🍽️"];

  const summary = (input.weekSummary || "").trim();
  if (summary) lines.push(summary);

  let wroteAnything = false;
  for (const type of MEAL_TYPES) {
    // #1543 — the shared fold, not `?? "dinner"`. Nullish-coalescing catches
    // null and undefined but NOT `""` or a non-canonical tag, so a mis-tagged
    // meal matched no type at all and was silently omitted from the text she
    // sends to other people. `mealTypeOf` folds it to dinner instead.
    const rows = input.meals.filter((m) => !m.removed && mealTypeOf(m) === type);
    if (rows.length === 0) continue;

    lines.push("");
    lines.push(`${MEAL_TYPE_LABELS[type]}:`);
    for (const m of rows) {
      const text = type === "school_lunch" ? schoolLunchText(m) : mealText(m);
      if (!text) continue;
      lines.push(m.day ? `${m.day}: ${text}` : text);
      wroteAnything = true;
    }
  }

  // Nothing plannable. Returning a header with no meals would be a text that
  // says nothing, so the caller checks this and does not offer to send.
  if (!wroteAnything) return "";

  lines.push("");
  lines.push("Sent from my Kin app 💜");

  return stripEmDashes(lines.join("\n")).trim();
}
